import { io, Socket } from 'socket.io-client';
import { BehaviorSubject } from 'rxjs';

export interface SocketConfig {
  url: string;
  namespace?: string;
  options?: {
    autoConnect?: boolean;
    transports?: string[];
    timeout?: number;
  };
}

export interface ConnectionStatus {
  connected: boolean;
  connecting: boolean;
  error?: string;
}

export class SocketService {
  private socket: Socket | null = null;
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>({
    connected: false,
    connecting: false,
  });

  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  constructor(private config: SocketConfig) {}

  /**
   * Connect to the Socket.io server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      this.updateConnectionStatus({ connected: false, connecting: true });

      const url = this.config.namespace
        ? `${this.config.url}/${this.config.namespace}`
        : this.config.url;

      this.socket = io(url, {
        autoConnect: this.config.options?.autoConnect ?? true,
        transports: this.config.options?.transports ?? ['websocket', 'polling'],
        timeout: this.config.options?.timeout ?? 20000,
      });

      this.socket.on('connect', () => {
        console.log('✅ Socket connected successfully');
        this.updateConnectionStatus({ connected: true, connecting: false });
        resolve();
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error);
        this.updateConnectionStatus({
          connected: false,
          connecting: false,
          error: error.message,
        });
        reject(error);
      });

      this.socket.on('disconnect', (reason) => {
        console.log('🔌 Socket disconnected:', reason);
        this.updateConnectionStatus({ connected: false, connecting: false });
      });
    });
  }

  /**
   * Disconnect from the Socket.io server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.updateConnectionStatus({ connected: false, connecting: false });
    }
  }

  /**
   * Check if connected to the server
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get the current socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Update connection status and notify subscribers
   */
  private updateConnectionStatus(status: ConnectionStatus): void {
    this.connectionStatusSubject.next(status);
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.disconnect();
    this.connectionStatusSubject.complete();
  }
}
